import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea.jsx';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, MapPin, Building2, Trash2, Edit } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: ''
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/locations`);
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Fehler beim Laden der Standorte';
      toast.error(errorMessage);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  // PLZ lookup for Germany
  const handlePostalCodeChange = async (postalCode, isEdit = false) => {
    if (postalCode.length === 5) {
      try {
        const response = await axios.get(`${API}/plz-lookup/${postalCode}`);
        if (response.data.city) {
          if (isEdit) {
            setEditingLocation(prev => ({ ...prev, city: response.data.city }));
          } else {
            setFormData(prev => ({ ...prev, city: response.data.city }));
          }
        }
      } catch (error) {
        // PLZ not found - ignore
      }
    }
  };

  // City autocomplete
  const handleCityInput = async (cityInput, isEdit = false) => {
    if (cityInput.length >= 3) {
      try {
        const response = await axios.get(`${API}/city-search?q=${encodeURIComponent(cityInput)}`);
        setCitySuggestions(response.data || []);
      } catch (error) {
        setCitySuggestions([]);
      }
    } else {
      setCitySuggestions([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Combine address
    const address = `${formData.street} ${formData.house_number}, ${formData.postal_code} ${formData.city}`;
    
    try {
      await axios.post(`${API}/admin/locations`, {
        name: formData.name,
        address: address,
        street: formData.street,
        house_number: formData.house_number,
        postal_code: formData.postal_code,
        city: formData.city
      });
      toast.success('Standort erfolgreich angelegt!');
      setDialogOpen(false);
      setFormData({ name: '', street: '', house_number: '', postal_code: '', city: '' });
      fetchLocations();
    } catch (error) {
      // Handle error properly - detail might be an array or object
      let errorMessage = 'Fehler beim Anlegen des Standorts';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'postal_code') {
      handlePostalCodeChange(value, false);
    }
    if (field === 'city') {
      handleCityInput(value, false);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation({
      ...location,
      street: location.street || '',
      house_number: location.house_number || '',
      postal_code: location.postal_code || '',
      city: location.city || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const address = `${editingLocation.street} ${editingLocation.house_number}, ${editingLocation.postal_code} ${editingLocation.city}`;
      await axios.put(`${API}/admin/locations/${editingLocation.id}`, {
        name: editingLocation.name,
        address: address,
        street: editingLocation.street,
        house_number: editingLocation.house_number,
        postal_code: editingLocation.postal_code,
        city: editingLocation.city
      });
      toast.success('Standort aktualisiert. Der Superadmin wurde benachrichtigt.');
      setEditDialogOpen(false);
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteRequest = async () => {
    if (!locationToDelete) return;
    
    try {
      await axios.post(`${API}/admin/deletion-request`, {
        location_id: locationToDelete.id,
        location_name: locationToDelete.name,
        reason: deleteReason || null
      });
      toast.success('Löschanfrage wurde an den Superadmin gesendet. Sie werden benachrichtigt, sobald die Anfrage bearbeitet wurde.');
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      setDeleteReason('');
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Fehler beim Senden der Löschanfrage';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="locations-title">
              Standortverwaltung
            </h1>
            <p className="text-muted-foreground mt-2">Verwalten Sie Ihre Tankstellen-Standorte</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-location-button">
                <Plus className="mr-2 h-4 w-4" />
                Standort hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Neuen Standort anlegen</DialogTitle>
                  <DialogDescription>
                    Fügen Sie einen neuen Tankstellen-Standort hinzu.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Standort-Name *</Label>
                    <Input
                      id="name"
                      placeholder="z.B. Tankstelle Hauptstraße"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="street">Straße *</Label>
                      <Input
                        id="street"
                        placeholder="Musterstraße"
                        value={formData.street}
                        onChange={(e) => handleChange('street', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="house_number">Nr. *</Label>
                      <Input
                        id="house_number"
                        placeholder="123"
                        value={formData.house_number}
                        onChange={(e) => handleChange('house_number', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">PLZ *</Label>
                      <Input
                        id="postal_code"
                        placeholder="12345"
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value)}
                        maxLength={5}
                        required
                      />
                    </div>
                    <div className="col-span-2 space-y-2 relative">
                      <Label htmlFor="city">Ort *</Label>
                      <Input
                        id="city"
                        placeholder="Musterstadt"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        required
                      />
                      {citySuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {citySuggestions.map((city, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-gray-100 dark:bg-gray-800 cursor-pointer text-sm"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, city: city }));
                                setCitySuggestions([]);
                              }}
                            >
                              {city}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-location-button"
                  >
                    Standort anlegen
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {locations.map((location) => (
            <Card key={location.id} data-testid={`location-card-${location.id}`}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{location.name}</CardTitle>
                    {location.address && (
                      <CardDescription className="mt-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span className="text-xs">{location.address}</span>
                        </div>
                      </CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Erstellt: {new Date(location.created_at).toLocaleDateString('de-DE')}
                  </p>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditLocation(location)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setLocationToDelete(location);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {locations.length === 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="py-12 text-center">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">Noch keine Standorte angelegt</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Klicken Sie auf "Standort hinzufügen" um zu beginnen
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Standort löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Sind Sie sicher, dass Sie den Standort "{locationToDelete?.name}" löschen möchten?
                <br /><br />
                <strong>Hinweis:</strong> Diese Anfrage wird an den Superadmin gesendet. Der Standort wird erst nach Genehmigung gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="delete-reason">Grund für Löschung (optional)</Label>
              <Textarea
                id="delete-reason"
                placeholder="Warum möchten Sie diesen Standort löschen?"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setLocationToDelete(null);
                setDeleteReason('');
              }}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteRequest}
                className="bg-red-600 hover:bg-red-700"
              >
                Löschanfrage senden
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Location Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Standort bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Standortdaten. Der Superadmin wird über Änderungen benachrichtigt.
              </DialogDescription>
            </DialogHeader>
            {editingLocation && (
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Standort-Name *</Label>
                  <Input
                    id="edit-name"
                    value={editingLocation.name || ''}
                    onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="edit-street">Straße *</Label>
                    <Input
                      id="edit-street"
                      value={editingLocation.street || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, street: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-house_number">Nr. *</Label>
                    <Input
                      id="edit-house_number"
                      value={editingLocation.house_number || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, house_number: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="edit-postal_code">PLZ *</Label>
                    <Input
                      id="edit-postal_code"
                      value={editingLocation.postal_code || ''}
                      onChange={(e) => {
                        setEditingLocation({ ...editingLocation, postal_code: e.target.value });
                        handlePostalCodeChange(e.target.value, true);
                      }}
                      maxLength={5}
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="edit-city">Ort *</Label>
                    <Input
                      id="edit-city"
                      value={editingLocation.city || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, city: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSaveEdit}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
